import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { fetchCompanySelfAssessmentUserDetail } from './companySelfAssessmentApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  ProgressList,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_COLORS,
  toneChip
} from '../../../../component/common/designSystem';
import { SCORE_RAMP, ATTACHMENT_RAMP } from './selfAssessmentTheme';

/**
 * Self-Assessment → User profile.
 *
 * One person's assessment history: how many they have finished, which ones they
 * come back to, where their scores land, and anything they started but never
 * submitted.
 *
 * Both outcome donuts are always rendered rather than swapped in and out. Most
 * people will have taken only one kind, and an empty "Attachment Styles" card
 * is itself the answer — it says they have not taken one, which a disappearing
 * panel would leave the reader guessing about.
 *
 * What is deliberately NOT here: the `user_details` captured before a run —
 * date of birth, ethnicity, job role, and the answers about mental-health
 * medication and recent GP visits. That is special-category data collected to
 * contextualise a result for the person taking it, not to be reported on. The
 * long `result_message` written for the individual is left out too; the outcome
 * label carries everything a history table needs.
 *
 * `fetcher`/`backPath`/`backLabel` are props so the super-admin Self-Assessment
 * hub can reuse the page against the unscoped endpoint.
 */
const SelfAssessmentUserDetail = ({
  fetcher = fetchCompanySelfAssessmentUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const scoreBands = detail?.scoreBands || [];
  const attachmentStyles = detail?.attachmentStyles || [];
  const topAssessments = detail?.topAssessments || [];
  const unfinished = detail?.unfinished || [];

  // Most recent first, the way every other history list in the panel reads.
  const history = [...(detail?.completionHistory || [])].reverse();

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.completions}
          title="Completed"
          value={user ? user.totalCompleted : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Different Assessments"
          value={user ? user.assessmentsTaken : '-'}
        />
        {/* Dash rather than 0% when they have only taken attachment
            assessments, which carry no score at all. */}
        <StatCard
          icon={STAT_ICONS.score}
          title="Average Score"
          value={user?.avgScore != null ? `${user.avgScore}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Still Open"
          value={user ? user.inProgress : '-'}
        />
        <StatCard icon={STAT_ICONS.logs} title="Abandoned" value={user ? user.left : '-'} />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Last Completed"
          value={user?.lastCompletedDate || '-'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {/* Their quartiles across every scored assessment they have taken.
            A shape, not a grade — direction belongs to the questionnaire. */}
        <MiniDonutCard
          title="Where Their Scores Land"
          subtitle="Scored assessments, by quartile"
          data={scoreBands}
          colors={SCORE_RAMP}
          unit="completions"
          totalLabel="Scored"
        />
        <MiniDonutCard
          title="Attachment Styles"
          subtitle="Results across attachment assessments"
          data={attachmentStyles}
          colors={ATTACHMENT_RAMP}
          unit="completions"
          totalLabel="Results"
          legendLimit={4}
        />

        <Panel title="Assessment History" subtitle="First and most recent completion">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First completion</td>
              <td className="py-2 text-right">{user?.firstCompletedDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last completion</td>
              <td className="py-2 text-right">{user?.lastCompletedDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Different assessments</td>
              <td className="py-2 text-right">{user ? user.assessmentsTaken : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Total completed</td>
              <td className="py-2 text-right">{user ? user.totalCompleted : '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Assessments They Return To" subtitle="Most often completed">
          {topAssessments.length > 0 ? (
            <ProgressList
              items={topAssessments.map((row) => ({ label: row.title, count: row.count }))}
              unit="times"
              color={CHART_COLORS.brand}
            />
          ) : (
            <PanelEmpty>No assessments completed yet.</PanelEmpty>
          )}
        </Panel>

        {/* Started and never submitted — the most actionable list on the page,
            and empty for most people, which is the point. */}
        <Panel title="Started but Not Finished" subtitle="Runs still sitting open">
          {unfinished.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Assessment</th>
                  <th className="py-2 font-medium text-right">Started</th>
                </>
              }
            >
              {unfinished.map((row) => (
                <tr key={`${row.title}-${row.startedOn}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.title}</td>
                  <td className="py-2 text-right">{row.startedOn || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>Nothing left half-finished.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="mt-6">
        <Panel title="Recent Completions" subtitle="Their last assessments, most recent first">
          {history.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium">Assessment</th>
                  <th className="py-2 font-medium text-right">Result</th>
                </>
              }
            >
              {history.map((row, index) => (
                <tr key={`${row.date}-${row.assessmentId}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.date || '-'}</td>
                  <td className="py-2 px-4">{row.assessmentTitle}</td>
                  <td className="py-2 text-right">{row.result || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No assessments completed yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

SelfAssessmentUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

SelfAssessmentUserDetail.defaultProps = {
  backLabel: 'Back to Employees'
};

export default SelfAssessmentUserDetail;
